import { useState, useEffect, useCallback } from 'react'
import { Activity, Wifi, WifiOff, AlertTriangle, Clock, ArrowUpCircle, ArrowDownCircle, ChevronDown, ChevronUp, Heart, Download, ExternalLink } from 'lucide-react'
import { adminApi } from '../services/ApiService'
import './DeviceLogs.css'

const HOUR_OPTIONS = [1, 6, 12, 24]

const RESET_REASON_LABELS = {
  1: 'Power-on', 2: 'Ext reset', 3: 'SW Reset', 4: 'WDT', 5: 'Deep sleep',
  6: 'Brownout', 7: 'SDIO', 8: 'Task WDT', 9: 'Multi WDT', 10: 'JTAG',
}
const getResetLabel = (code) => RESET_REASON_LABELS[code] || `rr=${code}`
const getResetColor = (code) => {
  if (code === 1 || code === 2 || code === 3 || code === 5) return '#94a3b8'
  if (code === 6) return '#f59e0b'
  if (code === 4 || code === 7 || code === 8 || code === 9) return '#ef4444'
  return '#94a3b8'
}

export default function DeviceLogs({ mac }) {
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [hours, setHours] = useState(24)
  const [expandedSection, setExpandedSection] = useState(null)

  const fetchStats = useCallback(async () => {
    if (!mac) return
    setLoading(true)
    setError(null)
    try {
      const data = await adminApi.get(`/admin/logs/device/${encodeURIComponent(mac)}/stats`, { hours })
      if (data.success) {
        setStats(data)
      } else {
        setError('No se pudieron obtener los logs')
      }
    } catch (err) {
      console.error('Error fetching device logs:', err)
      setError(err.message || 'Error al cargar logs')
    } finally {
      setLoading(false)
    }
  }, [mac, hours])

  useEffect(() => { fetchStats() }, [fetchStats])

  const formatTimestamp = (ts) => {
    if (!ts) return '—'
    const d = new Date(typeof ts === 'number' ? ts : ts)
    return d.toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' })
  }

  const formatRelative = (ts) => {
    if (!ts) return ''
    const diff = Date.now() - (typeof ts === 'number' ? ts : new Date(ts).getTime())
    const mins = Math.floor(diff / 60000)
    if (mins < 1) return 'hace instantes'
    if (mins < 60) return `hace ${mins}m`
    const hrs = Math.floor(mins / 60)
    if (hrs < 24) return `hace ${hrs}h`
    return `hace ${Math.floor(hrs / 24)}d`
  }

  const cleanMessage = (msg) => {
    if (!msg) return ''
    // Remove timestamp prefix and lambda noise
    return msg.replace(/^\d{4}-\d{2}-\d{2}T[\d:.Z]+\s+[\w-]+\s+\w+\s+/, '').trim().slice(0, 120)
  }

  const toggleSection = (section) => {
    setExpandedSection(prev => prev === section ? null : section)
  }

  if (loading && !stats) {
    return (
      <div className="info-section device-logs">
        <h3 className="section-title">
          <Activity className="w-4 h-4" style={{ display: 'inline', verticalAlign: 'middle', marginRight: '0.25rem' }} />
          Logs del dispositivo
        </h3>
        <div className="dl-loading">
          <div className="spinner" style={{ width: '1.5rem', height: '1.5rem' }}></div>
          <span>Cargando logs...</span>
        </div>
      </div>
    )
  }

  return (
    <div className="info-section device-logs">
      <div className="dl-header">
        <h3 className="section-title" style={{ marginBottom: 0, borderBottom: 'none', paddingBottom: 0 }}>
          <Activity className="w-4 h-4" style={{ display: 'inline', verticalAlign: 'middle', marginRight: '0.25rem' }} />
          Logs del dispositivo
        </h3>
        <div className="dl-time-selector">
          {HOUR_OPTIONS.map(h => (
            <button
              key={h}
              className={`dl-time-btn ${hours === h ? 'active' : ''}`}
              onClick={() => setHours(h)}
            >
              {h}h
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="dl-error">
          <AlertTriangle size={14} /> {error}
        </div>
      )}

      {stats && (
        <>
          {/* Stats cards */}
          <div className="dl-stats-grid">
            <div className="dl-stat-card dl-stat-connections">
              <ArrowUpCircle size={18} />
              <div>
                <span className="dl-stat-value">{stats.stats.connections}</span>
                <span className="dl-stat-label">Conexiones</span>
              </div>
            </div>
            <div className="dl-stat-card dl-stat-disconnections">
              <ArrowDownCircle size={18} />
              <div>
                <span className="dl-stat-value">{stats.stats.disconnections}</span>
                <span className="dl-stat-label">Desconexiones</span>
              </div>
            </div>
            <div className="dl-stat-card dl-stat-errors">
              <AlertTriangle size={18} />
              <div>
                <span className="dl-stat-value">{stats.stats.errors}</span>
                <span className="dl-stat-label">Errores</span>
              </div>
            </div>
            <div className="dl-stat-card dl-stat-heartbeats">
              <Heart size={18} />
              <div>
                <span className="dl-stat-value">{stats.stats.heartbeats || 0}</span>
                <span className="dl-stat-label">Heartbeats</span>
              </div>
            </div>
            <div className="dl-stat-card dl-stat-uptime">
              <Clock size={18} />
              <div>
                <span className="dl-stat-value">{stats.stats.avgUptime}</span>
                <span className="dl-stat-label">Uptime promedio</span>
              </div>
            </div>
          </div>

          {/* Crash counters warning */}
          {(stats.deviceInfo?.panicCount > 0 || stats.deviceInfo?.wdtCount >= 3) && (
            <div style={{
              background: 'rgba(239,68,68,0.1)',
              border: '1px solid rgba(239,68,68,0.35)',
              borderRadius: '8px',
              padding: '8px 12px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '0.8rem',
              color: '#ef4444',
            }}>
              <AlertTriangle size={13} style={{ flexShrink: 0 }} />
              <span>
                <strong>Recovery mode detectado</strong>:{stats.deviceInfo.panicCount > 0 ? ` ${stats.deviceInfo.panicCount} panic${stats.deviceInfo.panicCount > 1 ? 's' : ''}` : ''}{stats.deviceInfo.wdtCount >= 3 ? ` ${stats.deviceInfo.wdtCount} WDT resets` : ''}
              </span>
            </div>
          )}

          {/* Notice when no lifecycle events but heartbeats exist */}
          {stats.stats.connections === 0 && stats.stats.disconnections === 0 && stats.stats.heartbeats > 0 && (
            <div className="dl-notice">
              <Heart size={12} /> Sin eventos de conexión/desconexión en este período, pero se detectaron {stats.stats.heartbeats} heartbeats — el dispositivo estuvo activo.
            </div>
          )}

          {/* Recent events summary */}
          {stats.recentEvents && (
            <div className="dl-recent">
              {stats.recentEvents.lastConnection && (
                <div className="dl-recent-item dl-recent-connect">
                  <Wifi size={12} />
                  <span>Última conexión: {formatTimestamp(stats.recentEvents.lastConnection.timestamp)}</span>
                  <span className="dl-recent-relative">{formatRelative(stats.recentEvents.lastConnection.timestamp)}</span>
                </div>
              )}
              {stats.recentEvents.lastDisconnection && (
                <div className="dl-recent-item dl-recent-disconnect">
                  <WifiOff size={12} />
                  <span>Última desconexión: {formatTimestamp(stats.recentEvents.lastDisconnection.timestamp)}</span>
                  <span className="dl-recent-relative">{formatRelative(stats.recentEvents.lastDisconnection.timestamp)}</span>
                </div>
              )}
              {stats.recentEvents.lastError && (
                <div className="dl-recent-item dl-recent-error">
                  <AlertTriangle size={12} />
                  <span>Último error: {formatTimestamp(stats.recentEvents.lastError.timestamp)}</span>
                  <span className="dl-recent-relative">{formatRelative(stats.recentEvents.lastError.timestamp)}</span>
                </div>
              )}
              {stats.recentEvents.lastHeartbeat && (
                <div className="dl-recent-item dl-recent-heartbeat">
                  <Heart size={12} />
                  <span>Último heartbeat: {formatTimestamp(stats.recentEvents.lastHeartbeat.timestamp)}</span>
                  <span className="dl-recent-relative">{formatRelative(stats.recentEvents.lastHeartbeat.timestamp)}</span>
                </div>
              )}
              {stats.recentEvents.lastOta && (
                <div className="dl-recent-item dl-recent-ota">
                  <Download size={12} />
                  <span>
                    Último OTA enviado: {formatTimestamp(stats.recentEvents.lastOta.timestamp)}
                    {stats.recentEvents.lastOta.targetVersion && <strong> → v{stats.recentEvents.lastOta.targetVersion}</strong>}
                    {stats.recentEvents.lastOta.assetName && <span className="dl-ota-asset"> ({stats.recentEvents.lastOta.assetName})</span>}
                  </span>
                  <span className="dl-recent-relative">{formatRelative(stats.recentEvents.lastOta.timestamp)}</span>
                </div>
              )}
            </div>
          )}

          {/* Expandable log sections */}
          <div className="dl-sections">
            {[
              { key: 'connections', label: 'Conexiones', icon: <Wifi size={14} />, color: '#10b981', entries: stats.logs?.connections },
              { key: 'disconnections', label: 'Desconexiones', icon: <WifiOff size={14} />, color: '#f59e0b', entries: stats.logs?.disconnections },
              { key: 'errors', label: 'Errores', icon: <AlertTriangle size={14} />, color: '#ef4444', entries: stats.logs?.errors },
            ].filter(s => s.entries?.length > 0).map(section => (
              <div key={section.key} className="dl-section">
                <button className="dl-section-toggle" onClick={() => toggleSection(section.key)}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: section.color }}>
                    {section.icon}
                    {section.label} ({section.entries.length})
                  </span>
                  {expandedSection === section.key ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </button>
                {expandedSection === section.key && (
                  <div className="dl-log-list">
                    {section.entries.slice(0, 20).map((log, i) => (
                      <div key={log.eventId || i} className="dl-log-entry">
                        <span className="dl-log-time">{formatTimestamp(log.timestamp)}</span>
                        <span className="dl-log-msg">{cleanMessage(log.message)}</span>
                        {log.resetReason != null && (
                          <span style={{
                            fontSize: '0.7rem',
                            padding: '1px 6px',
                            borderRadius: '4px',
                            background: 'rgba(0,0,0,0.2)',
                            color: getResetColor(log.resetReason),
                            fontWeight: 600,
                            whiteSpace: 'nowrap',
                            flexShrink: 0,
                          }}>
                            {getResetLabel(log.resetReason)}
                          </span>
                        )}
                      </div>
                    ))}
                    {section.entries.length > 20 && (
                      <div className="dl-log-more">+{section.entries.length - 20} más</div>
                    )}
                  </div>
                )}
              </div>
            ))}

            {/* OTA commands section */}
            {stats.logs?.ota?.length > 0 && (
              <div className="dl-section">
                <button className="dl-section-toggle" onClick={() => toggleSection('ota')}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#818cf8' }}>
                    <Download size={14} />
                    OTA enviados ({stats.logs.ota.length})
                  </span>
                  {expandedSection === 'ota' ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </button>
                {expandedSection === 'ota' && (
                  <div className="dl-log-list">
                    {stats.logs.ota.map((ota, i) => (
                      <div key={ota.eventId || i} className="dl-log-entry dl-log-entry-ota">
                        <span className="dl-log-time">{formatTimestamp(ota.timestamp)}</span>
                        <span className="dl-log-msg">
                          OTA enviado
                          {ota.targetVersion && <strong style={{ color: '#818cf8', marginLeft: '0.3rem' }}>→ v{ota.targetVersion}</strong>}
                          {ota.assetName && <span style={{ color: '#94a3b8', marginLeft: '0.4rem', fontSize: '0.75rem' }}>{ota.assetName}</span>}
                        </span>
                        {ota.firmwareUrl && (
                          <a
                            href={ota.firmwareUrl}
                            target="_blank"
                            rel="noreferrer"
                            style={{ color: '#818cf8', flexShrink: 0, display: 'flex', alignItems: 'center' }}
                            title={ota.firmwareUrl}
                          >
                            <ExternalLink size={11} />
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}
